import { Box, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { getExcelResumeEligibility } from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventAlert, eventEmitter } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    fontSize: '14px',
    marginBottom: theme.spacing(0.5)
  },
  warn: {
    fontSize: '13px',
    color: theme.palette.error.main
  }
}))

// Excel manual early resume: pick a resume date within the pause window. A
// current-month resume charges coins (shown here); other months are free.
const ResumeForm = ({ isModalOpen, formData, onSaveClick, closeModal }) => {
  const classes = useStyles()
  const [resumeDate, setResumeDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [eligibility, setEligibility] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const hasFetchedRef = useRef(false)

  const fetchEligibility = async (date, initial) => {
    try {
      if (initial) setIsLoading(true)
      const response = await getExcelResumeEligibility(formData.uuid, date)
      const data = response?.data
      if (!data?.resume_eligible) {
        if (initial) {
          eventAlert(
            data?.reason ||
              data?.resume_ineligibility_reason ||
              'This enrolment is not eligible for resume.',
            constants.ERROR
          )
          closeModal()
        } else {
          setEligibility((prev) => ({ ...prev, ...data }))
        }
        return
      }
      setEligibility(data)
    } catch (err) {
      if (initial) {
        eventAlert('Unable to check resume eligibility.', constants.ERROR)
        closeModal()
      }
    } finally {
      if (initial) setIsLoading(false)
    }
  }

  const handleSave = () => {
    if (!resumeDate) {
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    onSaveClick({ resume_date: resumeDate }, 'resume_enrolment')
  }

  useEffect(() => {
    if (!isModalOpen || !formData?.uuid || hasFetchedRef.current) return
    hasFetchedRef.current = true
    fetchEligibility(resumeDate, true)
  }, [isModalOpen, formData?.uuid])

  useEffect(() => {
    if (!isModalOpen) hasFetchedRef.current = false
  }, [isModalOpen])

  const insufficient =
    eligibility &&
    eligibility.is_current_month &&
    eligibility.coins_sufficient === false

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      onSaveClick={handleSave}
      modalTitle="Resume enrolment"
      buttonLabel="Confirm"
      maxWidth="sm"
      loading={isLoading ? true : undefined}
    >
      <TextField
        className={classes.formControl}
        label="Resume date"
        type="date"
        value={resumeDate}
        onChange={(e) => {
          setResumeDate(e.target.value)
          if (e.target.value) fetchEligibility(e.target.value, false)
        }}
        variant="outlined"
        required
        InputLabelProps={{ shrink: true }}
        inputProps={{
          min: dayjs().format('YYYY-MM-DD'),
          max: eligibility?.pause_end_date || undefined
        }}
      />

      {eligibility && (
        <Box className={classes.infoBox}>
          {eligibility.pause_end_date && (
            <Typography className={classes.infoRow}>
              Pause ends:{' '}
              <b>{dayjs(eligibility.pause_end_date).format('DD MMM YYYY')}</b>
            </Typography>
          )}
          <Typography className={classes.infoRow}>
            {eligibility.payable_amount > 0
              ? `Amount payable to resume: ₹${eligibility.payable_amount}`
              : eligibility.coins_to_deduct > 0
              ? `Cost to resume: ${eligibility.coins_to_deduct} coins`
              : 'Free resume'}
          </Typography>
          {eligibility.remaining_coins != null && (
            <Typography className={classes.infoRow}>
              Wallet balance: <b>{eligibility.remaining_coins || 0} coins</b>
            </Typography>
          )}
          {insufficient && (
            <Typography className={classes.warn}>
              Insufficient coins — please top up before resuming.
            </Typography>
          )}
        </Box>
      )}
    </ModalForm>
  )
}

ResumeForm.propTypes = {
  isModalOpen: PropTypes.bool,
  formData: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default ResumeForm
